import { useState } from 'react';

import Form from '@/components/data-entry/Form';
import FullButton from '@/components/data-entry/FullButton';

import { FullInputEmail } from '@/components/data-entry/FullInputEmail';
import { usePMDRouter } from '@/hooks/usePMDRouter';
import { signIn } from '@/lib/auth';

export const OAuthLogIn = ({
  returnTo,
  onClick,
}: {
  returnTo?: string;
  onClick?: () => void;
}) => {
  const router = usePMDRouter();

  const subdomain = window.location.hostname.split('.')[0];
  const isE2E = subdomain === 'e2e';

  const [loggingWith, setLoggingWith] = useState('');
  const [emailLinkError, setEmailLinkError] = useState<string | null>(null);
  const [emailLinkSending, setEmailLinkSending] = useState(false);

  const handleEmailLogin = async (formValues: { email?: string }) => {
    const email = formValues.email?.trim();
    if (!email) {
      return;
    }

    setEmailLinkError(null);
    setEmailLinkSending(true);

    try {
      const result = await signIn('email', {
        callbackUrl: returnTo || window.location.origin,
        email,
        redirect: false,
      });

      if (result?.error) {
        const byCode: Record<string, string> = {
          AccessDenied:
            'We could not sign you in for this email. Please contact support if this keeps happening.',
          EmailSignin:
            'We could not send the sign-in email. Check the address and try again in a few minutes.',
        };
        setEmailLinkError(
          byCode[result.error] ??
            'Something went wrong requesting a sign-in link. Please try again.'
        );
        return;
      }

      if (result && result.ok === false) {
        setEmailLinkError(
          'We could not send the sign-in email. Check the address and try again.'
        );
        return;
      }

      router.push('/login-verify-link');
    } catch {
      setEmailLinkError(
        'Network error while requesting a sign-in link. Check your connection and try again.'
      );
    } finally {
      setEmailLinkSending(false);
    }
  };
  const handleOAuth = provider => {
    signIn(provider, {
      callbackUrl: returnTo || window.location.origin,
    });
  };

  return (
    <div data-testid="container-auth-form">
      <div className="flex flex-col">
        <Form onSubmit={handleEmailLogin}>
          {({ methods }) => (
            <>
              <FullInputEmail methods={methods} placeholder="Email" required />

              {emailLinkError ? (
                <p
                  className="text-destructive mt-2 text-sm"
                  role="alert"
                  data-testid="email-link-error"
                >
                  {emailLinkError}
                </p>
              ) : null}

              <FullButton
                block
                className="mt-4"
                data-testid="btn-continue-to-login"
                disabled={emailLinkSending}
              >
                {emailLinkSending ? 'Sending…' : 'Get Link'}
              </FullButton>
            </>
          )}
        </Form>

        <div className="text-center my-5">or</div>

        <FullButton
          size="lg"
          className="mb-4 text-primary bg-[#FFFFFF] border-primary hover:text-white hover:bg-[#4285F4] hover:border-[#4285F4]"
          onClick={() => {
            setLoggingWith('google');
            handleOAuth('google');
            if (onClick) onClick();
          }}
          startIcon="google"
          variant="outline"
        >
          {loggingWith === 'google'
            ? 'Authenticating ...'
            : 'Continue with Google'}
          <div />
        </FullButton>

        {isE2E && (
          <FullButton
            data-testid="btn-login-with-email"
            size="lg"
            className="mb-4 bg-surface border-[#4267b2] hover:bg-background hover:border-[#1B76F2] text-primary"
            onClick={() => {
              setLoggingWith('auth0');
              handleOAuth('auth0');
              if (onClick) onClick();
            }}
            startIcon="email"
            variant="outline"
          >
            {loggingWith === 'auth0'
              ? 'Authenticating ...'
              : 'Continue with Email'}
          </FullButton>
        )}
      </div>
    </div>
  );
};
